import { Center, Loader } from '@mantine/core';
import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router';

import { routes } from './routes';

// Loading fallback component
const PageLoader = () => (
  <Center h="100vh">
    <Loader size="lg" variant="dots" color="primary" />
  </Center>
);

// Relative <Navigate> targets resolve against the flat route tree, not the
// URL, and would drop the :project segment.
const ProjectRedirect = ({
  routeKey,
}: {
  routeKey: 'conversations' | 'coverageGaps' | 'tags';
}) => {
  const { project } = useParams();
  const { hash, search } = useLocation();
  if (!project) return <Navigate to="/" replace />;
  return (
    <Navigate
      to={`${routes[routeKey].build({ project })}${search}${hash}`}
      replace
    />
  );
};

// External links and bookmarks still carry the retired /setup suffix.
const LegacyIntegrationSetupRedirect = () => {
  const { project, id } = useParams();
  const { hash, search } = useLocation();
  if (!project || !id) return <Navigate to="/" replace />;
  return (
    <Navigate
      to={`${routes.integration.build({ project, id })}${search}${hash}`}
      replace
    />
  );
};

// The retired view and review paths both land on the source page, where a
// pending review opens its own dialog.
export const LegacySourceRedirect = () => {
  const { project, sourceId } = useParams();
  const { hash, search } = useLocation();
  if (!project || !sourceId) return <Navigate to="/" replace />;
  return (
    <Navigate
      to={`${routes.source.build({ project, sourceId })}${search}${hash}`}
      replace
    />
  );
};

// Root pages
const RootPage = lazy(() => import('../pages/index'));
const GetStarted = lazy(() => import('../pages/project/get-started/index'));

// Auth pages
const AuthIndex = lazy(() => import('../pages/auth/index'));
const SignIn = lazy(() => import('../pages/auth/signin'));
const SignUp = lazy(() => import('../pages/auth/signup'));
const VerifyOtp = lazy(() => import('../pages/auth/verify-otp'));
const AcceptInvite = lazy(() => import('../pages/auth/accept-invite'));

// Onboarding page
const Onboarding = lazy(() => import('../pages/onboarding/index'));

// Settings pages
const SettingsIndex = lazy(() => import('../pages/settings/index'));
const SettingsAccount = lazy(() => import('../pages/settings/account'));
const SettingsAuth = lazy(() => import('../pages/settings/auth'));
const SettingsProjects = lazy(() => import('../pages/settings/projects'));
const SettingsTeam = lazy(() => import('../pages/settings/team'));
const SettingsUsage = lazy(() => import('../pages/settings/usage'));

// Project pages
const ProjectIndex = lazy(() => import('../pages/project/index'));
const ApiKeys = lazy(() => import('../pages/project/api-keys/index'));
const Conversations = lazy(
  () => import('../pages/project/conversations/index'),
);
const ConversationDetail = lazy(
  () => import('../pages/project/conversations/thread-detail'),
);
const CoverageGaps = lazy(() => import('../pages/project/coverage-gaps/index'));
const CoverageGapDetail = lazy(
  () => import('../pages/project/coverage-gaps/detail'),
);
const CoverageGapClusterDetail = lazy(
  () => import('../pages/project/coverage-gaps/clusters/detail'),
);
const Integrations = lazy(() => import('../pages/project/integrations/index'));
const Playground = lazy(() => import('../pages/project/playground/index'));
const IntegrationDetail = lazy(
  () => import('../pages/project/integrations/detail'),
);
const IntegrationNew = lazy(() => import('../pages/project/integrations/new'));
const SourceAnalytics = lazy(
  () => import('../pages/project/source-analytics/index'),
);
const SourceGroups = lazy(() => import('../pages/project/source-groups/index'));
const Sources = lazy(() => import('../pages/project/sources/index'));
const SourceDetail = lazy(() => import('../pages/project/sources/detail'));
const SourceNew = lazy(() => import('../pages/project/sources/new'));
const SourceNewWebScrape = lazy(
  () => import('../pages/project/sources/new-web-scrape'),
);
const TagsManagement = lazy(() => import('../pages/project/tags/index'));
const TopQuestions = lazy(() => import('../pages/project/top-questions/index'));
const TopQuestionDetail = lazy(
  () => import('../pages/project/top-questions/detail'),
);
const TopQuestionClusterDetail = lazy(
  () => import('../pages/project/top-questions/clusters/detail'),
);
const Customizations = lazy(
  () => import('../pages/project/customizations/index'),
);
const CustomizationDetail = lazy(
  () => import('../pages/project/customizations/detail'),
);
const Users = lazy(() => import('../pages/project/users/index'));

// OAuth callback pages
const GoogleDriveOauthCallback = lazy(
  () => import('../pages/oauth/google-drive/callback'),
);
const SalesforceCasesOauthCallback = lazy(
  () => import('../pages/oauth/salesforce-cases/callback'),
);
const SharePointOauthCallback = lazy(
  () => import('../pages/oauth/sharepoint/callback'),
);
const SalesforceKnowledgeOauthCallback = lazy(
  () => import('../pages/oauth/salesforce-knowledge/callback'),
);
const ZendeskOauthCallback = lazy(
  () => import('../pages/oauth/zendesk/callback'),
);

// Thread redirect (resolves a threadId to its project and navigates to the
// thread viewer; used by deep links from SFD and similar flows).
const ThreadRedirect = lazy(() => import('../pages/thread'));

const NotFound = lazy(() => import('../pages/not-found'));

const AppRoutes = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Root */}
        <Route path="/" element={<RootPage />} />

        {/* Auth routes */}
        <Route path="/auth" element={<AuthIndex />} />
        <Route path="/auth/signin" element={<SignIn />} />
        <Route path="/auth/signup" element={<SignUp />} />
        <Route path="/auth/verify-otp" element={<VerifyOtp />} />
        {/* Under /auth so a logged-out invitee is not bounced to sign-in */}
        <Route path="/auth/accept-invite" element={<AcceptInvite />} />

        {/* Onboarding route */}
        <Route path={routes.onboarding.pattern} element={<Onboarding />} />

        {/* Settings routes */}
        <Route path={routes.settings.pattern} element={<SettingsIndex />} />
        <Route
          path={routes.settingsAccount.pattern}
          element={<SettingsAccount />}
        />
        <Route path={routes.settingsAuth.pattern} element={<SettingsAuth />} />
        <Route
          path={routes.settingsProjects.pattern}
          element={<SettingsProjects />}
        />
        <Route path={routes.settingsTeam.pattern} element={<SettingsTeam />} />
        <Route
          path={routes.settingsUsage.pattern}
          element={<SettingsUsage />}
        />

        {/* OAuth callback routes */}
        <Route
          path="/oauth/google-drive/callback"
          element={<GoogleDriveOauthCallback />}
        />
        <Route
          path="/oauth/salesforce-cases/callback"
          element={<SalesforceCasesOauthCallback />}
        />
        <Route
          path="/oauth/sharepoint/callback"
          element={<SharePointOauthCallback />}
        />
        <Route
          path="/oauth/salesforce-knowledge/callback"
          element={<SalesforceKnowledgeOauthCallback />}
        />
        <Route
          path="/oauth/zendesk/callback"
          element={<ZendeskOauthCallback />}
        />

        {/* Thread redirect */}
        <Route path="/thread/:threadId" element={<ThreadRedirect />} />

        {/* Project routes */}
        <Route path={routes.dashboard.pattern} element={<ProjectIndex />} />
        <Route path={routes.getStarted.pattern} element={<GetStarted />} />
        <Route path={routes.apiKeys.pattern} element={<ApiKeys />} />
        <Route path={routes.playground.pattern} element={<Playground />} />
        <Route
          path="/:project/common-questions"
          element={<ProjectRedirect routeKey="coverageGaps" />}
        />
        <Route
          path={routes.conversations.pattern}
          element={<Conversations />}
        />
        <Route
          path={routes.conversation.pattern}
          element={<ConversationDetail />}
        />
        <Route path={routes.coverageGaps.pattern} element={<CoverageGaps />} />
        <Route
          path={routes.coverageGapClusterSet.pattern}
          element={<CoverageGapDetail />}
        />
        <Route
          path={routes.coverageGapCluster.pattern}
          element={<CoverageGapClusterDetail />}
        />
        <Route
          path={routes.customizations.pattern}
          element={<Customizations />}
        />
        <Route
          path={routes.customization.pattern}
          element={<CustomizationDetail />}
        />
        <Route path={routes.integrations.pattern} element={<Integrations />} />
        <Route
          path={routes.integrationNew.pattern}
          element={<IntegrationNew />}
        />
        <Route
          path={routes.integration.pattern}
          element={<IntegrationDetail />}
        />
        <Route
          path="/:project/integrations/:id/setup"
          element={<LegacyIntegrationSetupRedirect />}
        />
        <Route
          path={routes.sourceAnalytics.pattern}
          element={<SourceAnalytics />}
        />
        <Route path={routes.sourceGroups.pattern} element={<SourceGroups />} />
        <Route path={routes.sources.pattern} element={<Sources />} />
        <Route path={routes.sourceNew.pattern} element={<SourceNew />} />
        <Route
          path={routes.sourceNewWebsite.pattern}
          element={<SourceNewWebScrape />}
        />
        <Route path={routes.source.pattern} element={<SourceDetail />} />
        <Route
          path={routes.sourceReview.pattern}
          element={<LegacySourceRedirect />}
        />
        <Route
          path={routes.sourceView.pattern}
          element={<LegacySourceRedirect />}
        />
        <Route path={routes.tags.pattern} element={<TagsManagement />} />
        <Route
          path="/:project/status-tags"
          element={<ProjectRedirect routeKey="tags" />}
        />
        <Route path={routes.topQuestions.pattern} element={<TopQuestions />} />
        <Route
          path={routes.topQuestionClusterSet.pattern}
          element={<TopQuestionDetail />}
        />
        <Route
          path={routes.topQuestionCluster.pattern}
          element={<TopQuestionClusterDetail />}
        />
        <Route path={routes.users.pattern} element={<Users />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
